import { MakeProblem, MakeProblemColl } from 'src/dto'
import { WorksheetDerivationResponse } from 'src/dto/Derivation'
import CheckableMap from 'src/models/CheckableMap'

import derivationApi from '@/api/derivation.api'
import worksheetApi from '@/api/worksheet.api'
import { WorkbookDerivationResponse } from '@/dto/Derivation/WorkbookDerivationResponse'
import type { DerivationWorksheetByStudentConceptChip } from '@/service/derivation/(filter)/(by)/derivationWorksheetByStudentConceptChip.type'
import type { DerivationWorksheetByWrongWorksheet } from '@/service/derivation/(filter)/(by)/derivationWorksheetByWrongWorksheet.type'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

class DerivationController implements Controller {
  adapter = {
    worksheetDerivation: makeAdapter({
      from(response: res.WorksheetDerivation): WorksheetDerivationResponse {
        return new WorksheetDerivationResponse(response)
      },
    }),
    workbookDerivation: makeAdapter({
      from(response: res.WorkbookDerivation): WorkbookDerivationResponse {
        return new WorkbookDerivationResponse(response)
      },
    }),
    pairSimilarProblem: makeAdapter({
      from(
        problem: res.PairSimilarProblem['problem'],
        favorite: res.PairSimilarProblem['pairProblemList'][number]['favorite'],
        tagTop:
          | res.PairSimilarProblem['pairProblemList'][number]['tagTop']
          | res.PairSimilarProblem['similarProblemList'][number]['tagTop'],
        curriculum: res.PairSimilarProblem['curriculum'],
      ) {
        return new MakeProblem({
          ...problem,
          rate: problem.problemSummary.answerRate,
          tagTop,
          favorite,
          defaultOrder: null,
          sequence: curriculum.sequence,
          conceptId: curriculum.conceptId,
          conceptName: curriculum.conceptName,
          littleChapterName: curriculum.littleChapterName,
          middleChapterName: curriculum.middleChapterName,
        })
      },
    }),
  }

  // n차 학습지 - 교재 문항
  fetchByWorkbookProblem = async (params: req.DerivationByWorkbookProblem) => {
    const {
      data: { data },
    } = await derivationApi.getByWorkbookProblem(params)

    return this.adapter.workbookDerivation.from(data)
  }

  // n차 학습지 - 교재 페이지
  fetchByWorkbookPage = async (params: req.DerivationByWorkbookPage) => {
    const {
      data: { data },
    } = await derivationApi.getByWorkbookPage(params)

    return this.adapter.workbookDerivation.from(data)
  }

  // 유형분석 학습지
  fetchByWeakConceptChip = async (params: req.ByWeakConceptChip) => {
    const {
      data: { data },
    } = await derivationApi.getByWeakConceptChip(params)

    return this.adapter.worksheetDerivation.from(data)
  }

  wrongWorksheet = {
    filterResponse: {
      fetchByWeakWorksheetFilter: async (
        filter:
          | DerivationWorksheetByWrongWorksheet.ByWeakWorksheetFilter.ByConcept
          | DerivationWorksheetByWrongWorksheet.ByWeakWorksheetFilter.ByProblem,
      ) => {
        if (filter.wrongWorksheetType === 'WRONG_CONCEPT_WORKSHEET') {
          if (filter.levelType !== 'NORMAL') {
            filter.preferPairOverSimilar = undefined
          }
        } else {
          if (filter.problemRequestType === 'SAME') {
            filter.excludePrevious = false
          }
        }

        const {
          data: { data },
        } = await derivationApi.wrongWorksheet.filterResponse.getByWeakWorksheetFilter(filter, {
          useAbortController: true,
        })

        return this.adapter.worksheetDerivation.from(data)
      },
      fetchByCasProblem: async (
        filter: DerivationWorksheetByWrongWorksheet.ByCasFilter,
      ) => {
        if (filter.wrongWorksheetType === 'WRONG_CONCEPT_WORKSHEET') {
          if (filter.levelType !== 'NORMAL') {
            filter.preferPairOverSimilar = undefined
          }
        } else {
          if (filter.problemRequestType === 'SAME') {
            filter.excludePrevious = false
          }
        }

        const {
          data: { data },
        } = await derivationApi.wrongWorksheet.filterResponse.getByCasProblem(filter, {
          useAbortController: true,
        })

        return this.adapter.worksheetDerivation.from(data)
      },
    },
  }

  achievement = {
    filterResponse: {
      byStudent: async (
        params:
          | DerivationWorksheetByStudentConceptChip.Filter.ByConcept
          | DerivationWorksheetByStudentConceptChip.Filter.ByProblem,
      ) => {
        const {
          data: { data },
        } = await derivationApi.achievement.filterResponse.byStudent(params)
        return this.adapter.worksheetDerivation.from(data)
      },
    },
  }

  // n차 학습지 - 단원
  fetchByWorksheetChapter = async <
    T extends res.WrongWorksheetType,
    U = T extends 'WRONG_CONCEPT_WORKSHEET' ? '유형수' | '정답률' : never,
  >(
    params: req.ByWorksheetChapter<T, U>,
  ) => {
    const {
      data: { data },
    } = await derivationApi.getByWorksheetChapter<T, U>(params)

    return this.adapter.worksheetDerivation.from(data)
  }
  // n차 학습지 - 날짜
  fetchByWorksheetDateRange = async (params: req.ByWorksheetDateRange) => {
    const {
      data: { data },
    } = await derivationApi.getByWorksheetDateRange(params)

    return this.adapter.worksheetDerivation.from(data)
  }

  // n차 학습지 - 원클릭
  fetchByOneClick = async (params: req.ByOneClick) => {
    const {
      data: { data },
    } = await derivationApi.getByOneClick(params)
    return this.adapter.worksheetDerivation.from(data)
  }

  fetchFilterIdByWorksheet = async (data: req.CreateFilterByWeakWorksheet) => {
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterIdByWorksheet(data)
    return filterId
  }

  fetchFilterIdByConcept = async (data: req.CreateFilterByWeakConcept) => {
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterIdByConcept(data)
    return filterId
  }

  fetchFilterIdByPeriod = async (data: req.CreateFilterByWeakPeriod) => {
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterIdByPeriod(data)
    return filterId
  }
  // 쌍둥이&유사문제 문제조회 : 백엔드 도메인따라가는 것이지만.. 원래는 problem도메인이고, 추후 바뀔 예정이라함..
  /**
   * @deprecated
   * 리뉴얼된 교재에서는 절대 쓰지 말것
   */
  async _fetchPairSimilarProblemList(
    id: res.Problem<'WORKBOOK' | 'WORKSHEET'>['id'],
    params: req.PairSimilarProblem,
  ) {
    const { data } = await derivationApi._postPairSimilarProblemList(id, params)
    const similar = data.similarProblemList.map(({ problem, favorite, tagTop }) =>
      this.adapter.pairSimilarProblem.from(problem, favorite, tagTop, data.curriculum),
    )
    const pair = data.pairProblemList.map(({ problem, favorite, tagTop }) => {
      return this.adapter.pairSimilarProblem.from(problem, favorite, tagTop, data.curriculum)
    })

    return {
      similarProblemColl: new MakeProblemColl(CheckableMap.fromArr(similar, 'id')),
      pairProblemColl: new MakeProblemColl(CheckableMap.fromArr(pair, 'id')),
      curriculum: data.curriculum,
    }
  }

  fetchFilterIdByOneClick = async (data: req.CreateFilterByOneClick) => {
    const {
      data: {
        data: { filterId },
      },
    } = await worksheetApi.createFilterIdByOneClick(data)
    return filterId
  }
}

export const derivationController = new DerivationController()
